// Regras de telefone do lead que o BANCO aplica, espelhadas em TS.
//
// Elas existem para o update otimista do TanStack Query não divergir do que os triggers de
// `lead_phones` vão gravar. Se estas funções e o banco discordarem, a UI pisca com um estado
// que não é o real — por isso cada uma referencia o objeto de banco que imita.
//
// Ficam em `src/lib/**` de propósito: é o único diretório (junto de `src/hooks/**`) coberto
// pelo Vitest — ver vitest.config.ts.

import { normalizePhone } from '@/lib/sonax/normalizePhone';
import type { MultiValueEntry } from '@/types/fieldRegistry';

export interface LeadPhone {
  id: string;
  lead_id: string;
  phone: string;
  label: string;
  is_primary: boolean;
  is_whatsapp: boolean | null;
  source: string;
  created_at: string;
}

/**
 * Forma canônica aceita por `lead_phones`: 12 dígitos (fixo) ou 13 (móvel).
 * Espelha o CHECK `lead_phones_phone_digits_chk` e o trigger `trg_lead_phones_normalize`.
 * Fora dessa faixa `normalize_phone` nem seria idempotente — prefixaria outro `55`.
 */
export function isStorablePhone(raw: string | null | undefined): boolean {
  const canonical = normalizePhone(raw);
  return !!canonical && /^[0-9]{12,13}$/.test(canonical);
}

/** Mensagem de validação para a UI, ou null quando o número é gravável. */
export function validateLeadPhone(raw: string): string | null {
  if (!raw.trim()) return null; // vazio é "ainda não preenchido", não erro
  return isStorablePhone(raw) ? null : 'Telefone inválido — informe DDD + 8 ou 9 dígitos.';
}

/**
 * Normaliza, descarta o que não é gravável e deduplica pelo valor canônico, preservando a
 * primeira ocorrência. Espelha o que `public.set_lead_phones` faz antes de gravar — inclusive
 * o caso de planilha que repete o mesmo número em "Telefone" e "WhatsApp".
 */
export function dedupeEntries(entries: MultiValueEntry[]): MultiValueEntry[] {
  const seen = new Set<string>();
  const out: MultiValueEntry[] = [];
  for (const entry of entries) {
    const canonical = normalizePhone(entry.value);
    if (!canonical || !/^[0-9]{12,13}$/.test(canonical)) continue;
    if (seen.has(canonical)) continue;
    seen.add(canonical);
    out.push({ ...entry, value: canonical });
  }
  return out;
}

/**
 * Garante exatamente um principal: o marcado de menor índice ou, na ausência, o primeiro.
 * Espelha o índice parcial `lead_phones_one_primary_uidx` mais a escolha de
 * `public.set_lead_phones`.
 */
export function ensureSinglePrimary(entries: MultiValueEntry[]): MultiValueEntry[] {
  if (entries.length === 0) return entries;
  const chosen = entries.findIndex((e) => e.is_primary);
  const primaryIndex = chosen >= 0 ? chosen : 0;
  return entries.map((entry, i) => ({ ...entry, is_primary: i === primaryIndex }));
}

/** Pipeline completo aplicado antes de mandar para a RPC. */
export function prepareForSave(entries: MultiValueEntry[]): MultiValueEntry[] {
  return ensureSinglePrimary(dedupeEntries(entries));
}

/**
 * Desmembra o canônico no par legado `phone_country_code` + `phone` que os quatro chamadores do
 * `LeadForm` concatenam para gravar o espelho `leads.phone`. Como `normalizePhone` sempre devolve
 * `55` + 10/11 dígitos, o DDI é fixo — o campo de DDI do formulário só existia porque o valor
 * chegava cru, e a faixa canônica 12–13 já não aceitava outro país mesmo antes desta entrega.
 */
export function splitCanonical(canonical: string | null | undefined): {
  countryCode: string;
  national: string;
} {
  const digits = (canonical || '').replace(/\D/g, '');
  if (!digits) return { countryCode: '55', national: '' };
  return digits.startsWith('55') && digits.length > 2
    ? { countryCode: '55', national: digits.substring(2) }
    : { countryCode: '55', national: digits };
}

/**
 * Hidrata a lista a partir do par legado, para o formulário abrir preenchido quando o chamador
 * ainda passa só `phone` (`LinkLeadToDealModal`) ou quando o lead não tem linha em `lead_phones`
 * — caso dos 12.383 leads com lixo no campo `phone` que o backfill deixou de fora.
 */
export function entriesFromLegacyPhone(phone: string | null | undefined): MultiValueEntry[] {
  // Sem concatenar DDI: `normalizePhone` já prefixa o 55 quando faltam, e prefixar por fora
  // transformaria um telefone-lixo de 9 dígitos num número de 13 que NÃO EXISTE — foi assim que
  // a Fase 2 gerou linhas artefato em produção. O que não cai na faixa canônica some.
  const canonical = normalizePhone(phone);
  // Testa a FAIXA no valor já normalizado em vez de chamar `isStorablePhone`: re-normalizar
  // fora de 12–13 prefixa outro 55 e um lixo de 11 dígitos passaria a "válido" de 13.
  if (!canonical || !/^[0-9]{12,13}$/.test(canonical)) return [];
  return [{ value: canonical, label: 'Celular', is_primary: true }];
}

/**
 * Assinatura do conjunto já preparado, para saber se o usuário mexeu nos telefones.
 * Sem isso a edição regravaria o mesmo conjunto a cada "Salvar", e o `ON CONFLICT DO UPDATE`
 * de `set_lead_phones` sobrescreveria `is_whatsapp` com o `null` que o formulário não coleta.
 */
export function phonesSignature(entries: MultiValueEntry[]): string {
  return prepareForSave(entries)
    .map((e) => `${e.value}:${e.label}${e.is_primary ? ':*' : ''}`)
    .join('|');
}

/**
 * Se vale o round-trip do `set_lead_phones` na CRIAÇÃO. Com um telefone só não vale: o trigger
 * `trg_leads_sync_lead_phones` já criou a linha a partir de `leads.phone` — mesma decisão da
 * Fase 4 no motor de importação.
 */
export function needsPhonesRoundTrip(entries: MultiValueEntry[]): boolean {
  return prepareForSave(entries).length > 1;
}

/**
 * Qual telefone vai parar em `leads.phone`. Espelha `fn_lead_phones_sync_leads`: o principal,
 * ou `null` quando não há nenhum — caso em que o trigger PRESERVA o valor antigo em vez de
 * apagar, então quem chama não deve gravar null por conta própria.
 */
export function primaryPhoneOf(entries: MultiValueEntry[]): string | null {
  return entries.find((e) => e.is_primary)?.value ?? null;
}

/** Linhas do banco → entradas do editor, principal primeiro e o resto por antiguidade. */
export function toEntries(rows: LeadPhone[]): MultiValueEntry[] {
  return [...rows]
    .sort((a, b) => {
      if (a.is_primary !== b.is_primary) return a.is_primary ? -1 : 1;
      return a.created_at.localeCompare(b.created_at);
    })
    .map((row) => ({ value: row.phone, label: row.label, is_primary: row.is_primary }));
}

/**
 * Sucessor promovido quando o principal é removido: o mais antigo restante.
 * Espelha o trecho de promoção de `fn_lead_phones_sync_leads`.
 */
export function promoteAfterRemoval(rows: LeadPhone[], removedId: string): LeadPhone[] {
  const remaining = rows.filter((r) => r.id !== removedId);
  if (remaining.length === 0) return remaining;
  const removed = rows.find((r) => r.id === removedId);
  if (!removed?.is_primary) return remaining;

  const successor = [...remaining].sort((a, b) => a.created_at.localeCompare(b.created_at))[0];
  return remaining.map((r) => ({ ...r, is_primary: r.id === successor.id }));
}
